import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('checking');
  const [business, setBusiness] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const businessId = searchParams.get('business_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.status === 'paid' || response.data.status === 'completed') {
          setStatus('success');
          
          if (businessId) {
            const bizResponse = await axios.get(`${API}/businesses/${businessId}`);
            setBusiness(bizResponse.data);
          }
        } else if (response.data.status === 'expired' || response.data.status === 'failed') {
          setStatus('failed');
        } else if (pollAttempts < 10) {
          // Continue polling
          setTimeout(() => setPollAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch (error) {
        console.error('Status check error:', error);
        if (pollAttempts < 5) {
          setTimeout(() => setPollAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, businessId, pollAttempts]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="p-4 border-b">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl">lokalnie.pro</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {status === 'checking' && 'Weryfikacja płatności...'}
                {status === 'success' && 'Płatność zakończona!'}
                {status === 'failed' && 'Płatność nie powiodła się'}
                {status === 'timeout' && 'Sprawdzanie płatności'}
                {status === 'error' && 'Błąd weryfikacji'}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              {status === 'checking' && (
                <div className="py-8">
                  <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">Sprawdzanie statusu płatności...</p>
                </div>
              )}

              {status === 'success' && (
                <div className="py-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-slate-600 mb-6">
                    Twoja strona została aktywowana!
                  </p>
                  
                  {business && (
                    <div className="bg-slate-100 rounded-lg p-4 mb-6 text-left">
                      <p className="font-semibold">{business.name}</p>
                      {business.subdomain && (
                        <p className="text-sm text-amber-600">
                          {business.subdomain}.lokalnie.pro
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {businessId && (
                      <Link to={`/landing/${businessId}`}>
                        <Button className="w-full bg-amber-500 hover:bg-amber-600">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Zobacz swoją stronę
                        </Button>
                      </Link>
                    )}
                    <Link to="/dashboard">
                      <Button variant="outline" className="w-full">
                        Przejdź do panelu
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {(status === 'failed' || status === 'error') && (
                <div className="py-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">✕</span>
                  </div>
                  <p className="text-slate-600 mb-6">
                    Przepraszamy, wystąpił problem z płatnością.
                  </p>
                  <Link to={businessId ? `/pricing?business_id=${businessId}` : '/pricing'}>
                    <Button className="w-full">Spróbuj ponownie</Button>
                  </Link>
                </div>
              )}

              {status === 'timeout' && (
                <div className="py-4">
                  <p className="text-slate-600 mb-6">
                    Płatność jest przetwarzana. Sprawdź swój email dla potwierdzenia.
                  </p>
                  <Link to="/dashboard">
                    <Button className="w-full">Przejdź do panelu</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
